<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useNotificationStore } from '@/stores/notification';
import { useBoletoStore } from '@/stores/boletos';

const notificationStore = useNotificationStore();
const boletoStore = useBoletoStore();

// Estado da tela
const selectedApi = ref('ummsg'); // Agora padrão é ummsg
const phoneNumber = ref(''); // Novo campo para o número de telefone de teste
const phoneNumberRules = [
  v => !v || /^\d{10,11}$/.test(v.replace(/\D/g, '')) || 'Número de telefone inválido'
];

// Estados para controle da interface
const showWhatsappTemplates = ref(true); // Sempre mostrar pois agora é o padrão
const showParametersSection = ref(false);

// Opções para o select de período
const getPreviousMonthYear = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${year}`;
};

const periodOptions = [
  { title: 'Todos os períodos', value: 'todos' },
  { title: getPreviousMonthYear(), value: getPreviousMonthYear() },
  { title: 'Teste', value: 'teste' }
];

// Opções para o select de API - agora apenas ummsg é suportado
const apiOptions = [
  { title: '1msg (WhatsApp Business)', value: 'ummsg' }
];

// Referências computadas ao store
const loading = computed(() => boletoStore.loading);

// Templates e parâmetros do WhatsApp
const whatsappTemplates = computed(() => boletoStore.whatsappTemplates);
const selectedWhatsappTemplate = computed({
  get: () => boletoStore.selectedWhatsappTemplate,
  set: (value) => {
    boletoStore.setWhatsappTemplate(value);
  }
});

const availableParameters = computed(() => boletoStore.availableParameters);
const selectedParameters = computed({
  get: () => boletoStore.selectedParameters,
  set: (value) => {
    boletoStore.setSelectedParameters(value);
  }
});

// Computed property para obter o nome da API selecionada
const selectedApiName = computed(() => {
  return '1msg (WhatsApp Business)';
});

// Computed para obter o template selecionado e sua quantidade de parâmetros
const selectedTemplateObject = computed(() => {
  if (!selectedWhatsappTemplate.value || !whatsappTemplates.value.length) {
    return null;
  }
  return whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
});

const requiredParametersCount = computed(() => {
  return selectedTemplateObject.value ? selectedTemplateObject.value.parameters_count : 0;
});

// Watch para carregar templates do WhatsApp automaticamente
watch(selectedApi, async (newApi) => {
  await boletoStore.buscarTemplatesWhatsapp('ummsg');
  await boletoStore.buscarParametrosDisponiveis('COBRANCA_POS');
  showParametersSection.value = true;
});

// Watch para carregar parâmetros quando selecionar template do WhatsApp
watch(selectedWhatsappTemplate, async (newTemplate) => {
  if (newTemplate) {
    // Limpar parâmetros selecionados ao mudar template
    selectedParameters.value = [];
  }
});

// Watch para validar quantidade de parâmetros em tempo real
watch(selectedParameters, (newParams) => {
  if (requiredParametersCount.value > 0 && newParams.length > requiredParametersCount.value) {
    // Se selecionou mais parâmetros que o necessário, manter apenas os primeiros
    selectedParameters.value = newParams.slice(0, requiredParametersCount.value);
    notificationStore.showNotification(
      `Este template aceita apenas ${requiredParametersCount.value} parâmetro(s). Os parâmetros extras foram removidos.`, 
      'warning'
    );
  }
});

const selectedPeriod = computed({
  get: () => boletoStore.selectedPeriod,
  set: (value) => {
    boletoStore.setPeriod(value);
    // Resetar o número de telefone quando mudar de período
    if (value !== 'teste') {
      phoneNumber.value = '';
    }
  }
});

// Visualizar template
const templatePreview = ref(null);

// Função para obter preview do template do WhatsApp com parâmetros
const getTemplatePreview = () => {
  if (!selectedWhatsappTemplate.value || !whatsappTemplates.value.length) {
    return '';
  }
  
  const template = whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
  if (!template) return '';
  
  let preview = template.text;
  
  // Substituir os placeholders {{n}} pelos parâmetros selecionados
  selectedParameters.value.forEach((param, index) => {
    const placeholder = `{{${index + 1}}}`;
    if (param) {
      const parameterDescription = availableParameters.value[param] || param;
      preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), `[${param}: ${parameterDescription}]`);
    }
  });
  
  // Converter \n para quebras de linha reais
  return preview.replace(/\\n/g, '\n');
};

// Função para obter placeholders do template
const getTemplatePlaceholders = () => {
  if (!selectedWhatsappTemplate.value || !whatsappTemplates.value.length) {
    return [];
  }
  
  const template = whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
  if (!template) return [];
  
  const placeholders = [];
  const regex = /\{\{(\d+)\}\}/g;
  let match;
  
  while ((match = regex.exec(template.text)) !== null) {
    const number = parseInt(match[1]);
    if (!placeholders.find(p => p.number === number)) {
      placeholders.push({
        number: number,
        placeholder: match[0]
      });
    }
  }
  
  return placeholders.sort((a, b) => a.number - b.number);
};

// Computed para placeholders do template
const templatePlaceholders = computed(() => getTemplatePlaceholders());

// Função para renderizar o template com selects
const renderTemplateWithSelects = () => {
  if (!selectedWhatsappTemplate.value || !whatsappTemplates.value.length) {
    return '';
  }
  
  const template = whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
  if (!template) return '';
  
  return template.text.replace(/\\n/g, '\n');
};

// Função para atualizar parâmetro em posição específica
const updateParameterAtPosition = (position, value) => {
  const newParams = [...selectedParameters.value];
  newParams[position - 1] = value;
  selectedParameters.value = newParams;
};

// Separa uma linha em partes de texto e placeholders
const parseLineWithPlaceholders = (line) => {
  const parts = [];
  const placeholderRegex = /\{\{(\d+)\}\}/g;
  let lastIndex = 0;
  let match;
  
  while ((match = placeholderRegex.exec(line)) !== null) {
    // Adiciona texto antes do placeholder
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: line.substring(lastIndex, match.index)
      });
    }
    
    // Adiciona o placeholder
    parts.push({
      type: 'placeholder',
      content: match[0],
      number: parseInt(match[1])
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Adiciona texto restante após o último placeholder
  if (lastIndex < line.length) {
    parts.push({
      type: 'text',
      content: line.substring(lastIndex)
    });
  }
  
  return parts;
};

// Computed para verificar se todos os placeholders foram preenchidos
const allPlaceholdersFilled = computed(() => {
  const placeholders = templatePlaceholders.value;
  if (placeholders.length === 0) return true;
  
  return placeholders.every(placeholder => {
    return selectedParameters.value[placeholder.number - 1];
  });
});

// Computed para obter parâmetros disponíveis para cada posição (excluindo os já selecionados)
const getAvailableParametersForPosition = (currentPosition) => {
  const allParams = Object.keys(availableParameters.value);
  const usedParams = selectedParameters.value.filter((param, index) => {
    // Não excluir o parâmetro da posição atual
    return param && (index + 1) !== currentPosition;
  });
  
  return allParams.filter(param => !usedParams.includes(param));
};

// Função para limpar todos os parâmetros
const clearAllParameters = () => {
  selectedParameters.value = [];
  notificationStore.showNotification('Todos os parâmetros foram limpos', 'info');
};

// Atualizar preview quando o template selecionado mudar
const updateTemplatePreview = () => {
  // Atualizar preview do template do WhatsApp
  templatePreview.value = getTemplatePreview();
};

// Watch para atualizar preview
watch(whatsappTemplates, () => {
  updateTemplatePreview();
});

watch(selectedWhatsappTemplate, () => {
  updateTemplatePreview();
});

watch(selectedParameters, () => {
  updateTemplatePreview();
});

// Estado para o dialog de confirmação
const confirmDialog = ref(false);
const confirmationMessage = ref('');

// Estado para o resultado da operação
const operationResult = ref(null);
const showResult = ref(false);

// Helper para verificar se é período de teste
const isTestPeriod = computed(() => {
  return selectedPeriod.value === 'teste';
});

// Preparar para envio e mostrar diálogo de confirmação
const prepareToSend = () => {
  // Validar se template do WhatsApp foi selecionado
  if (!selectedWhatsappTemplate.value) {
    notificationStore.showNotification('Por favor, selecione um template do WhatsApp', 'warning');
    return;
  }

  // Verificar se todos os placeholders necessários foram preenchidos
  if (!allPlaceholdersFilled.value) {
    notificationStore.showNotification('Por favor, configure todos os parâmetros necessários do template', 'warning');
    return;
  }
  
  // Validar número de telefone para período de teste
  if (isTestPeriod.value) {
    const phoneNumberOnly = phoneNumber.value.replace(/\D/g, '');
    if (!phoneNumberOnly || !/^\d{10,11}$/.test(phoneNumberOnly)) {
      notificationStore.showNotification('Por favor, informe um número de telefone válido para o teste', 'warning');
      return;
    }
  }
  
  // Obter template selecionado para exibir nome
  const template = whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
  const templateName = template ? template.code : selectedWhatsappTemplate.value;
  
  // Filtrar apenas os parâmetros que foram preenchidos
  const filledParameters = selectedParameters.value.filter(param => param);
  
  // Criar mensagem de confirmação
  const periodoText = selectedPeriod.value === 'todos' ? 'todos os períodos' : (
    selectedPeriod.value === 'teste' ? 
    `teste para o número ${phoneNumber.value}` : 
    `período ${selectedPeriod.value}`
  );
  
  let confirmText = `Você está prestes a enviar boletos de ${periodoText} utilizando o template "${templateName}" via ${selectedApiName.value}.`;
  
  if (filledParameters.length > 0) {
    confirmText += `\n\nParâmetros: ${filledParameters.join(', ')}`;
  }

  confirmText += `\n\n${selectedPeriod.value === 'teste' ? 
    'Esta ação enviará uma mensagem de teste para o número informado.' : 
    'Esta ação enviará mensagens de WhatsApp para todos os revendedores com cobranças em aberto no período selecionado.'}

Deseja continuar?`;
  
  confirmationMessage.value = confirmText;
  
  // Mostrar diálogo de confirmação
  confirmDialog.value = true;
};

// Confirmar e enviar
const confirmAndSend = async () => {
  confirmDialog.value = false; // Fechar diálogo
  
  // Filtrar apenas os parâmetros que foram preenchidos
  const filledParameters = selectedParameters.value.filter(param => param);
  
  // Obter template selecionado para exibir nome
  const template = whatsappTemplates.value.find(t => t.code === selectedWhatsappTemplate.value);
  const templateName = template ? template.name : selectedWhatsappTemplate.value;
  
  // Executar envio com os novos parâmetros
  const result = await boletoStore.enviarBoletos(
    selectedPeriod.value, 
    selectedApi.value, 
    isTestPeriod.value ? phoneNumber.value : null,
    selectedWhatsappTemplate.value,
    filledParameters
  );
  
  if (result) {
    // Armazenar e exibir resultado da operação
    operationResult.value = {
      message: result.message || 'Mensagens enviadas com sucesso',
      periodo: selectedPeriod.value === 'todos' ? 'Todos os períodos' : 
              (selectedPeriod.value === 'teste' ? `Teste (${phoneNumber.value})` : selectedPeriod.value),
      provedor: selectedApiName.value,
      whatsapp_template: templateName,
      parametros: filledParameters,
      total_revendedores: result.total_revendedores || 0,
      total_numeros: result.total_numeros || 0,
      total_chunks: result.total_chunks || 0,
      batch_ids: result.batch_ids || [],
      status: result.status || 'completed',
      tempo_inicio: result.tempo_inicio || '',
      tempo_estimado_conclusao: result.tempo_estimado_conclusao || '',
      intervalo_entre_mensagens: result.intervalo_entre_mensagens || '',
      timestamp: new Date().toLocaleString()
    };
    showResult.value = true;
    
    // Rolar para a seção de resultado
    setTimeout(() => {
      const resultElement = document.getElementById('operation-result');
      if (resultElement) {
        resultElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }
};

// Limpar resultado da operação
const clearOperationResult = () => {
  showResult.value = false;
  operationResult.value = null;
};

// Inicializar componente
onMounted(async () => {
  // Carregar templates do WhatsApp automaticamente
  await boletoStore.buscarTemplatesWhatsapp('ummsg');
  await boletoStore.buscarParametrosDisponiveis('COBRANCA_POS');
  showParametersSection.value = true;
  
  // Force update the preview after a short delay to ensure templates are loaded
  setTimeout(() => {
    updateTemplatePreview();
  }, 100);
});
</script>

<template>
  <div>
    <v-container fluid>
      <!-- Cabeçalho e título -->
      <div class="d-flex align-center justify-space-between m-0">
        <h1 class="text-h4 m-0">Envio de Boletos via WhatsApp</h1>
      </div>

      <!-- Resultado da operação -->
      <v-expand-transition>
        <v-card 
          v-if="showResult && operationResult" 
          variant="outlined" 
          color="success" 
          class="mb-6"
          id="operation-result"
        >
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-icon 
                  icon="mdi-check-circle" 
                  color="success" 
                  size="large" 
                  class="mr-3"
                ></v-icon>
                <div>
                  <h3 class="text-h6 mb-1">
                    Operação realizada com sucesso
                  </h3>
                  <p class="mb-0">{{ operationResult.message }}</p>
                </div>
              </div>
              <v-btn 
                icon="mdi-close" 
                variant="text" 
                density="compact" 
                @click="clearOperationResult"
              ></v-btn>
            </div>
          </v-card-text>
          
          <v-divider></v-divider>
          
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-list density="compact">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-calendar"></v-icon>
                    </template>
                    <v-list-item-title>Período</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.periodo }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-whatsapp"></v-icon>
                    </template>
                    <v-list-item-title>Template WhatsApp</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.whatsapp_template }}</v-list-item-subtitle>
                  </v-list-item>

                  <v-list-item v-if="operationResult.parametros && operationResult.parametros.length > 0">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-variable"></v-icon>
                    </template>
                    <v-list-item-title>Parâmetros</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.parametros.join(', ') }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="operationResult.tempo_inicio">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-clock-start"></v-icon>
                    </template>
                    <v-list-item-title>Início</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.tempo_inicio }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="operationResult.tempo_estimado_conclusao">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-clock-end"></v-icon>
                    </template>
                    <v-list-item-title>Conclusão estimada</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.tempo_estimado_conclusao }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-list density="compact">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-whatsapp"></v-icon>
                    </template>
                    <v-list-item-title>Provedor</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.provedor }}</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-account-group"></v-icon>
                    </template>
                    <v-list-item-title>Revendedores</v-list-item-title>
                    <v-list-item-subtitle class="font-weight-bold">
                      {{ operationResult.total_revendedores }} revendedores
                    </v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="operationResult.total_numeros">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-cellphone"></v-icon>
                    </template>
                    <v-list-item-title>Números</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.total_numeros }} telefones</v-list-item-subtitle>
                  </v-list-item>
                  
                  <v-list-item v-if="operationResult.intervalo_entre_mensagens">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-timer-outline"></v-icon>
                    </template>
                    <v-list-item-title>Intervalo</v-list-item-title>
                    <v-list-item-subtitle>{{ operationResult.intervalo_entre_mensagens }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
            
            <div class="d-flex justify-end mt-2">
              <p class="text-caption text-grey">Operação realizada em {{ operationResult.timestamp }}</p>
            </div>
          </v-card-text>
        </v-card>
      </v-expand-transition>

      <!-- Filtros e controles -->
      <v-card class="mb-6" variant="outlined">
        <v-card-text>
          <v-row>
            <!-- Seleção de período -->
            <v-col :cols="isTestPeriod ? 6 : 12" md="6">
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                label="Período"
                variant="outlined"
                density="comfortable"
                item-title="title"
                item-value="value"
                hide-details
              ></v-select>
            </v-col>
            
            <!-- Campo de telefone para teste -->
            <v-col v-if="isTestPeriod" cols="6" md="6">
              <v-text-field
                v-model="phoneNumber"
                label="Número de telefone para teste"
                variant="outlined"
                density="comfortable"
                hint="Informe apenas números (com DDD)"
                persistent-hint
                :rules="phoneNumberRules"
                v-mask="'(##) #####-####'"
                required
              ></v-text-field>
            </v-col>

            <!-- Seleção de API (agora só ummsg) -->
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedApi"
                :items="apiOptions"
                label="Método de envio"
                variant="outlined"
                density="comfortable"
                item-title="title"
                item-value="value"
                disabled
                hide-details
                hint="Usando WhatsApp Business API via 1msg"
                persistent-hint
              ></v-select>
            </v-col>
            
            <!-- Templates do WhatsApp -->
            <v-col cols="12" class="mt-4">
              <v-divider class="mb-4"></v-divider>
              <h3 class="text-h6 mb-3">
                <v-icon class="mr-2">mdi-whatsapp</v-icon>
                Templates do WhatsApp
              </h3>
              
              <v-select
                v-model="selectedWhatsappTemplate"
                :items="whatsappTemplates"
                label="Template do WhatsApp"
                variant="outlined"
                density="comfortable"
                item-value="code"
                item-title="code"
                :loading="loading"
                :disabled="loading || whatsappTemplates.length === 0"
                hint="Selecione o template validado pelo Meta"
                persistent-hint
              >
                <template v-slot:item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template v-slot:title>
                      <div class="d-flex justify-space-between align-center">
                        <span>{{ item.raw.code }}</span>
                        <v-chip size="small" color="primary">{{ item.raw.category }}</v-chip>
                      </div>
                    </template>
                    <template v-slot:subtitle>
                      <div class="mt-2">
                        <p class="text-caption mb-1">{{ item.raw.parameters_count }} parâmetros</p>
                        <pre class="text-caption" style="white-space: pre-wrap; font-family: inherit;">{{ item.raw.text.replace(/\\n/g, '\n') }}</pre>
                      </div>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <!-- Seção de Parâmetros -->
            <v-col v-if="showParametersSection && selectedWhatsappTemplate" cols="12">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-subtitle-1">
                  <v-icon class="mr-2">mdi-cog</v-icon>
                  Configuração da Mensagem
                </div>
                <div class="d-flex align-center gap-2">
                  <v-chip 
                    size="small" 
                    :color="allPlaceholdersFilled ? 'success' : 'warning'"
                  >
                    {{ templatePlaceholders.filter(p => selectedParameters[p.number - 1]).length }} / {{ templatePlaceholders.length }} configurados
                  </v-chip>
                  <v-btn
                    v-if="selectedParameters.some(p => p)"
                    size="small"
                    color="error"
                    variant="outlined"
                    prepend-icon="mdi-delete-sweep"
                    @click="clearAllParameters"
                  >
                    Limpar
                  </v-btn>
                </div>
              </div>
              
              <!-- Aviso sobre configuração -->
              <v-alert
                v-if="templatePlaceholders.length > 0"
                :type="allPlaceholdersFilled ? 'success' : 'info'"
                variant="tonal"
                class="mb-4"
                density="compact"
              >
                <template v-if="allPlaceholdersFilled">
                  ✅ Todos os parâmetros foram configurados
                </template>
                <template v-else>
                  Configure os parâmetros nos campos destacados abaixo para personalizar sua mensagem.
                </template>
              </v-alert>
              
              <!-- Template com selects inline ou mensagem completa -->
              <v-card variant="outlined" class="message-builder-card mb-4">
                <v-card-title class="text-subtitle-1 bg-blue-grey-lighten-4">
                  <v-icon class="mr-2">mdi-message-text-outline</v-icon>
                  {{ templatePlaceholders.length > 0 ? 'Monte sua mensagem' : 'Mensagem Final' }}
                </v-card-title>
                <v-card-text class="pa-4">
                  <!-- Se template tem parâmetros, mostrar com selects inline -->
                  <div v-if="templatePlaceholders.length > 0" class="message-builder">
                    <template v-for="(line, lineIndex) in renderTemplateWithSelects().split('\n')" :key="lineIndex">
                      <div class="message-line">
                        <template v-for="(part, partIndex) in parseLineWithPlaceholders(line)" :key="partIndex">
                          <span v-if="part.type === 'text'" class="message-text">{{ part.content }}</span>
                          <div v-else-if="part.type === 'placeholder'" class="placeholder-container">
                            <v-select
                              :model-value="selectedParameters[part.number - 1]"
                              @update:model-value="updateParameterAtPosition(part.number, $event)"
                              :items="getAvailableParametersForPosition(part.number)"
                              :label="`Parâmetro ${part.number}`"
                              variant="outlined"
                              density="compact"
                              hide-details
                              class="placeholder-select"
                              :placeholder="`Selecione para {{${part.number}}}`"
                              clearable
                              :disabled="getAvailableParametersForPosition(part.number).length === 0 && !selectedParameters[part.number - 1]"
                            >
                              <template v-slot:item="{ item, props }">
                                <v-list-item v-bind="props">
                                  <template v-slot:title>
                                    <div class="font-weight-bold">{{ item.raw }}</div>
                                  </template>
                                  <template v-slot:subtitle>
                                    <div class="text-caption">{{ availableParameters[item.raw] }}</div>
                                  </template>
                                </v-list-item>
                              </template>
                              <template v-slot:selection="{ item }">
                                <div class="d-flex align-center">
                                  <v-icon size="small" color="success" class="mr-1">mdi-check-circle</v-icon>
                                  <span class="font-weight-medium">{{ item.raw }}</span>
                                </div>
                              </template>
                              <template v-slot:no-data>
                                <v-list-item>
                                  <v-list-item-title class="text-grey">
                                    {{ selectedParameters.filter(p => p).length === Object.keys(availableParameters).length ? 
                                       'Todos os parâmetros foram usados' : 
                                       'Nenhum parâmetro disponível' }}
                                  </v-list-item-title>
                                </v-list-item>
                              </template>
                            </v-select>
                          </div>
                        </template>
                      </div>
                      <br v-if="lineIndex < renderTemplateWithSelects().split('\n').length - 1">
                    </template>
                  </div>
                  
                  <!-- Se template não tem parâmetros, mostrar mensagem completa -->
                  <div v-else class="message-final">
                    <v-alert type="success" variant="tonal" density="compact" class="mb-3">
                      ✅ Este template não possui parâmetros configuráveis
                    </v-alert>
                    <pre class="template-preview-enhanced">{{ renderTemplateWithSelects() }}</pre>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Botão de envio -->
            <v-col cols="12" class="d-flex align-center mt-4">
              <v-btn
                color="success"
                block
                :loading="loading"
                :disabled="!selectedWhatsappTemplate || !allPlaceholdersFilled || loading"
                prepend-icon="mdi-send"
                size="large"
                @click="prepareToSend"
              >
                <template v-if="requiredParametersCount > 0 && selectedParameters.length !== requiredParametersCount">
                  Selecione {{ requiredParametersCount }} parâmetro(s)
                </template>
                <template v-else>
                  Enviar Boletos via WhatsApp
                </template>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </v-container>
    
    <!-- Diálogo de confirmação -->
    <v-dialog
      v-model="confirmDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
          Confirmação de envio
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 white-space-pre-line">{{ confirmationMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="confirmDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            :loading="loading"
            @click="confirmAndSend"
          >
            Confirmar e Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.template-preview {
  white-space: pre-wrap;
  font-family: inherit;
  line-height: 1.5;
  padding: 8px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #ddd;
  max-height: 200px;
  overflow-y: auto;
}

.template-preview-enhanced {
  white-space: pre-wrap;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #e3f2fd;
  max-height: 300px;
  overflow-y: auto;
  color: #1a1a1a;
  font-size: 14px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.preview-card {
  border: 2px solid var(--v-primary-base) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selected-template {
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.white-space-pre-line {
  white-space: pre-line;
}

#operation-result {
  transition: all 0.5s ease;
  box-shadow: 0 4px 12px rgba(0, 120, 0, 0.15);
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.batch-ids-container {
  max-height: 80px;
  overflow-y: auto;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

#operation-result.v-card--variant-outlined.bg-info {
  border-left: 4px solid rgb(var(--v-theme-info));
  box-shadow: 0 4px 12px rgba(0, 120, 255, 0.15);
}

/* Novos estilos para o message builder */
.message-builder-card {
  border: 2px solid #e3f2fd !important;
}

.message-builder {
  font-family: 'Roboto', sans-serif;
  line-height: 1.8;
  font-size: 16px;
}


.placeholder-container {
  display: inline-flex;
  min-width: 200px;
  margin: 0 4px;
}

.placeholder-select {
  background: rgba(33, 150, 243, 0.1);
  border-radius: 4px;
}

.placeholder-select :deep(.v-field) {
  background: rgba(33, 150, 243, 0.05);
  border: 1px solid #2196F3;
}

.placeholder-select :deep(.v-field--focused) {
  background: rgba(33, 150, 243, 0.1);
  border-color: #1976D2;
}

.gap-2 {
  gap: 8px;
}
</style>
